import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Check, Building2, Plus, X } from "lucide-react";

function AddInline({ placeholder, onAdd, testid }) {
  const [v, setV] = useState("");
  const add = async () => { if (!v.trim()) return; if (await onAdd(v.trim())) setV(""); };
  return (
    <div className="flex gap-2">
      <Input value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder} onKeyDown={(e) => e.key === "Enter" && add()} data-testid={`${testid}-input`} className="h-9" />
      <Button type="button" size="sm" variant="outline" onClick={add} disabled={!v.trim()} data-testid={`${testid}-btn`}><Plus className="h-4 w-4" /></Button>
    </div>
  );
}

const call = async (fn, msg) => { try { await fn(); toast.success(msg); return true; } catch (e) { toast.error(e.response?.data?.detail || "Failed"); return false; } };

export function DepartmentPicker({ catalog, selected, onChange, onReload }) {
  const toggle = (k) => onChange(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]);
  const addDept = async (name) => {
    let key = null;
    const ok = await call(async () => { const r = await api.post("/org/departments", { name }); key = r.data.department.key; }, "Department added");
    if (ok) { onChange([...selected, key]); await onReload(); }
    return ok;
  };
  const delDept = async (key) => { if (await call(() => api.delete(`/org/departments/${key}`), "Department removed")) { onChange(selected.filter((x) => x !== key)); onReload(); } };
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="department-picker">
        {catalog.map((d) => {
          const on = selected.includes(d.key);
          return (
            <div key={d.key} className={`relative flex items-start gap-3 rounded-2xl border-2 p-4 transition-colors ${on ? "border-[#0D5C3A] bg-[#F0ECE1]" : "border-[#E5E0D8] bg-white hover:border-[#84A98C]"}`}>
              <button type="button" onClick={() => toggle(d.key)} data-testid={`dept-toggle-${d.key}`} className="flex flex-1 items-start gap-3 text-left">
                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${on ? "bg-[#0D5C3A] text-white" : "bg-[#FAF8F5] text-stone-400"}`}>
                  {on ? <Check className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
                </div>
                <div>
                  <p className="font-heading font-semibold text-stone-900">{d.name}{d.custom && <span className="ml-1 text-[10px] font-bold uppercase text-[#C85A32]">custom</span>}</p>
                  <p className={`text-xs ${d.roles.length ? "text-stone-500" : "font-medium text-[#C85A32]"}`}>{d.roles.length ? `${d.roles.length} roles · ${d.roles.map((r) => r.name).slice(0, 2).join(", ")}` : "No roles yet — add one in the Roles step"}</p>
                </div>
              </button>
              {d.custom && <button type="button" onClick={() => delDept(d.key)} className="text-stone-400 hover:text-rose-600" data-testid={`dept-delete-${d.key}`}><X className="h-4 w-4" /></button>}
            </div>
          );
        })}
      </div>
      <div className="max-w-md"><p className="mb-1 text-xs font-semibold text-stone-500">Need another department?</p><AddInline placeholder="Custom department name" onAdd={addDept} testid="add-custom-dept" /></div>
      <p className="text-xs text-stone-400">Selecting departments is optional — skip it and every department stays available.</p>
    </div>
  );
}

export function RolePicker({ catalog, departments, roles, onChange, onReload }) {
  const shown = catalog.filter((d) => !departments.length || departments.includes(d.key));
  const cur = (d) => (roles[d.key] && roles[d.key].length ? roles[d.key] : d.roles.map((r) => r.name));
  const toggle = (d, rn) => {
    const c = cur(d);
    onChange({ ...roles, [d.key]: c.includes(rn) ? c.filter((x) => x !== rn) : [...c, rn] });
  };
  const addRole = async (d, name) => {
    const ok = await call(() => api.post(`/org/departments/${d.key}/roles`, { name }), "Role added");
    if (ok) { onChange({ ...roles, [d.key]: [...cur(d), name] }); await onReload(); }
    return ok;
  };
  const delRole = async (d, name) => {
    if (await call(() => api.delete(`/org/departments/${d.key}/roles/${encodeURIComponent(name)}`), "Role removed")) {
      onChange({ ...roles, [d.key]: cur(d).filter((x) => x !== name) }); onReload();
    }
  };
  return (
    <div className="space-y-4" data-testid="role-picker">
      {shown.map((d) => (
        <div key={d.key} className="rounded-2xl border border-[#E5E0D8] bg-white p-4" data-testid={`role-group-${d.key}`}>
          <p className="font-heading font-semibold text-stone-900">{d.name}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {d.roles.map((r) => {
              const on = cur(d).includes(r.name);
              return (
                <span key={r.name} className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "border-[#0D5C3A] bg-[#0D5C3A] text-white" : "border-[#E5E0D8] bg-[#FAF8F5] text-stone-500"}`}>
                  <button type="button" onClick={() => toggle(d, r.name)} data-testid={`role-toggle-${d.key}-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{on && <Check className="mr-1 inline h-3 w-3" />}{r.name}</button>
                  {r.custom && <button type="button" onClick={() => delRole(d, r.name)} className="opacity-70 hover:opacity-100" data-testid={`role-delete-${d.key}-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}><X className="h-3 w-3" /></button>}
                </span>
              );
            })}
          </div>
          <div className="mt-3 max-w-sm"><AddInline placeholder="Add a custom role" onAdd={(n) => addRole(d, n)} testid={`add-custom-role-${d.key}`} /></div>
        </div>
      ))}
      <p className="text-xs text-stone-400">Several employees can share a role, and one person can hold several roles.</p>
    </div>
  );
}
